import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import type { OrderType } from '@/store/blotter/api/types/BlotterOrderEntryFromBackend.ts';
import { type Side, userTimeZone } from '@/store/orders/state.ts';
import { formatUserInputtedDate } from '@/utils/date.ts';

interface SummaryProps {
  account: string | null;
  orderType: OrderType;
  way: Side | null;
  notional: number | null;
  currency: string | null;
  limitPrice: number | null;
  isGtc: boolean | null;
  expiryDay: string | null;
  expiryTime: string | null;
  currencyPair: string | null;
  fixingRef: string | null;
  priceType: 'MID' | 'BIDASK' | null;
  fixingDate: string | null;
  fixingPlaceOrTime: string | null;
  finalStatus: boolean;
}

type TextIds =
  | 'order.form.summary.text.limit'
  | 'order.form.summary.text.call'
  | 'order.form.summary.text.fixing'
  | 'order.form.summary.text.algo';

function getSummaryId(orderType: OrderType): TextIds | undefined {
  switch (orderType) {
    case 'Twap':
    case 'Falcon':
    case 'Nightjar':
      return 'order.form.summary.text.algo';
    case 'Call':
      return 'order.form.summary.text.call';
    case 'Fixing':
      return 'order.form.summary.text.fixing';
    case 'StopLoss':
    case 'TakeProfit':
      return 'order.form.summary.text.limit';
  }
}

function isProperWay(way: string | null): way is Side {
  return way === 'Buy' || way === 'Sell' || way === 'Both';
}

export const Summary = ({
  way,
  orderType,
  notional,
  expiryDay,
  expiryTime,
  fixingDate,
  priceType,
  account: clientName,
  currency: notionalCurrency,
  finalStatus,
  ...props
}: SummaryProps) => {
  const { locale } = useIntl();
  if (!isProperWay(way) && orderType !== 'Call') {
    return null;
  }

  if (props.isGtc === null) {
    return null;
  }

  return (
    <span data-e2e="summary-text" className="p-0">
      <FormattedMessage
        id={getSummaryId(orderType)}
        values={{
          way:
            way === null || props.currencyPair === null ? null : (
              <FormattedMessage id={`order.form.way.${getOrderWay(props.currencyPair, notionalCurrency, way)}`} />
            ),
          submitted: true,
          finalStatus,
          clientName,
          notionalCurrency,
          orderType: <FormattedMessage id={`order.form.orderType.${orderType}Order`} />,
          notional: notional === null ? null : <FormattedNumber value={Number(notional)} />,
          expiryDay: formatUserInputtedDate(expiryDay, locale),
          expiryTime: expiryTime === null ? null : `${expiryTime} (${userTimeZone})`,
          fixingDate: formatUserInputtedDate(fixingDate, locale),
          priceType: priceType === null ? null : <FormattedMessage id={`order.form.priceType.${priceType}`} />,
          ...props,
        }}
      />
    </span>
  );
};

export function getOrderWay(currencyPair: string, notionalCurrency: string | null, way: Side): Side {
  const currencies = currencyPair?.split('/');

  return notionalCurrency === currencies[0] ? way : invertSide(way);
}

function invertSide(way: Side) {
  switch (way) {
    case 'Both':
      return 'Both';
    case 'Buy':
      return 'Sell';
    case 'Sell':
      return 'Buy';
  }
}
